import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  SelectChangeEvent,
} from '@mui/material';
import { ArrowBack, ArrowForward, Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { OrganizationFormData } from '../../organizations/types/organizationTypes';
import { FarmOrganizationData } from '../types/farmTypes';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { COLORS } from '../../../constants';
import CoordinateSelectorDialog from './CoordinateSelectorDialog';
import FarmBasicInfoStep from './FarmBasicInfoStep';
import FarmLocationContactStep from './FarmLocationContactStep';

interface FarmOrganizationFormProps {
  initialData: OrganizationFormData;
  onSubmit: (data: FarmOrganizationData) => void;
  onBack: () => void;
  isLoading?: boolean;
}

const FarmOrganizationForm: React.FC<FarmOrganizationFormProps> = ({
  initialData,
  onSubmit,
  onBack,
  isLoading = false,
}) => {
  const { t } = useTranslation('forms');
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    t('farm.steps.basic_info', 'Informações Básicas'),
    t('farm.steps.location_contact', 'Localização e Contato')
  ];

  const [formData, setFormData] = useState<FarmOrganizationData>({
    ...initialData,
    area_hectares: undefined,
    farm_type: undefined,
    capacity_head: undefined,
    registration_number: '',
    production_focus: '',
    country_code: 'BR',
    is_favorite: false,
    location: {
      address: '',
      region: '',
      postal_code: '',
      coordinates: {
        latitude: 0,
        longitude: 0,
      },
    },
    facilities: [],
    certifications: [],
    contact_info: {
      phone: '',
      email: '',
      emergency_contact: '',
    },
    is_active: true,
  });

  const [isMapOpen, setIsMapOpen] = useState(false);
  const isWithinBrazil = (lat: number, lon: number) => lat >= -34 && lat <= 6 && lon >= -75 && lon <= -34;
  const handleOpenMap = () => setIsMapOpen(true);
  const handleCloseMap = () => setIsMapOpen(false);
  const handleMapConfirm = (lat: number, lon: number) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        coordinates: { latitude: lat, longitude: lon }
      }
    } as FarmOrganizationData));
    setIsMapOpen(false);
  };
  const handleInputChange = (field: keyof FarmOrganizationData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'number' ? 
      (event.target.value ? Number(event.target.value) : undefined) : 
      event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field: keyof FarmOrganizationData) => (
    event: SelectChangeEvent<string> | any
  ) => {
    const value = typeof event.target.value === 'string' ? event.target.value : event.target.value[0] || '';
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCertificationChange = (event: SelectChangeEvent<string[]>) => {
    const value = typeof event.target.value === 'string' ? 
      event.target.value.split(',') : 
      event.target.value;
    setFormData(prev => ({ ...prev, certifications: value }));
  };

  const handleLocationChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        coordinates: prev.location?.coordinates || { latitude: 0, longitude: 0 },
        [field]: event.target.value || ''
      }
    } as FarmOrganizationData));
  };

  const handleContactChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      contact_info: {
        ...prev.contact_info,
        [field]: event.target.value || ''
      }
    } as FarmOrganizationData));
  };

  const handleFacilityChange = (facility: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => {
      const currentFacilities = Array.isArray(prev.facilities) ? prev.facilities : [];
      const updatedFacilities = event.target.checked
        ? [...currentFacilities, facility]
        : currentFacilities.filter(f => f !== facility);
      
      return {
        ...prev,
        facilities: updatedFacilities
      } as FarmOrganizationData;
    });
  };

  const handleCheckboxChange = (field: keyof FarmOrganizationData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: event.target.checked }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (activeStep === steps.length - 1) {
      onSubmit(formData);
    } else {
      handleNext();
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onBack();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const isStepValid = (step: number) => {
    if (step === 0) {
      return (
        !!formData.name &&
        !!formData.area_hectares &&
        !!formData.capacity_head &&
        !!formData.farm_type &&
        !!formData.production_focus &&
        !!formData.country_code
      );
    } else if (step === 1) {
      const coords = formData.location?.coordinates;
      const hasCoords = coords && typeof coords.latitude === 'number' && typeof coords.longitude === 'number';
      const validCoords = hasCoords ? isWithinBrazil(coords.latitude, coords.longitude) : false;
      return (
        validCoords &&
        !!formData.contact_info?.phone &&
        !!formData.contact_info?.email
      );
    }
    return false;
  };

  const isFormValid = () => {
    return isStepValid(activeStep);
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ 
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 1,
        maxWidth: '800px',
        mx: 'auto'
      }}
    >
      {/* Stepper */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        pr: 0.5,
        maxHeight: 'calc(100vh - 160px)',
        '&::-webkit-scrollbar': {
          width: '3px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '1px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '1px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        {/* Farm Specific Fields */}
        <Box sx={{ display: 'grid', gap: 1.5, p: 1 }}>
          {activeStep === 0 && (
            <FarmBasicInfoStep
              formData={formData}
              handleInputChange={handleInputChange}
              handleSelectChange={handleSelectChange}
              handleFacilityChange={handleFacilityChange}
            />
          )}

          {activeStep === 1 && (
            <FarmLocationContactStep
              formData={formData}
              handleLocationChange={handleLocationChange}
              handleContactChange={handleContactChange}
              handleOpenMap={handleOpenMap}
            />
          )}
        </Box>
      </Box>

      {/* Actions */}
      <Paper 
        elevation={0}
        sx={{
          p: 1,
          mt: 1,
          backgroundColor: COLORS.WHITE,
          border: `1px solid ${COLORS.CATSKILL_WHITE_BETA}`,
          borderRadius: 2,
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={isLoading}
            size="large"
            startIcon={<ArrowBack />}
            sx={{
              borderColor: DESIGN_TOKENS.colors.organization.farm,
              color: DESIGN_TOKENS.colors.organization.farm,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              '&:hover': {
                borderColor: DESIGN_TOKENS.colors.organization.farm,
                backgroundColor: `${DESIGN_TOKENS.colors.organization.farm}08`,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${DESIGN_TOKENS.colors.organization.farm}20`
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {activeStep === 0 ? t('farm.actions.back') : t('farm.actions.previous', 'Voltar')}
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            disabled={!isStepValid(activeStep) || isLoading}
            size="large"
            endIcon={activeStep === steps.length - 1 ? (isLoading ? undefined : <Check />) : <ArrowForward />}
            sx={{
              backgroundColor: DESIGN_TOKENS.colors.organization.farm,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              boxShadow: `0 2px 8px ${DESIGN_TOKENS.colors.organization.farm}30`,
              '&:hover': {
                backgroundColor: DESIGN_TOKENS.colors.organization.farm,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 16px ${DESIGN_TOKENS.colors.organization.farm}40`
              },
              '&:disabled': {
                backgroundColor: DESIGN_TOKENS.colors.neutral[100],
                color: DESIGN_TOKENS.colors.text.disabled,
                boxShadow: 'none'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {activeStep === steps.length - 1 ? (isLoading ? t('farm.actions.creating') : t('farm.actions.create')) : t('farm.actions.next', 'Próximo')}
          </Button>
        </Box>
      </Paper>
      <CoordinateSelectorDialog
        open={isMapOpen}
        onClose={handleCloseMap}
        onConfirm={handleMapConfirm}
        initialCoordinates={formData.location?.coordinates}
      />
    </Box>
  );
};

export default FarmOrganizationForm;
